import axios from 'axios';
import { useEffect, useState } from 'react';
import { weather, astronomy, openWeather } from './api/weather';
import { airQualityStatus, windQualityStatus, humidityStatus, temperatureStatus, generateRandomIconNumber } from './helpers';
import DonutChart from './components/Charts/DonutChart';
import LineChart from './components/Charts/LineChart';
import StatusChart from './components/Charts/StatusChart';
import LayoutGrid from './components/Layout/LayoutGrid';
import LayoutPanel from './components/Layout/LayoutPanel';
import CurrentWeather from './components/UI/CurrentWeather';
import LoadingElement from './components/Loading/LoadingElement';
import Table from './components/UI/Table';
import Tabs from './components/UI/Tabs';
import WeatherCard from './components/UI/WeatherCard';

export default function App() {
	const [weatherForecastData, setWeatherForecastData] = useState([]);
	const [currentSeaTemperature, setCurrentSeaTemperature] = useState(0);
	const [currentSoilTemperature, setCurrentSoilTemperature] = useState([]);
	const [regionalForecastData, setRegionalForecastData] = useState([]);
	const [currentUvIndexData, setCurrentUvIndexData] = useState(0);
	const [currentForecastData, setCurrentForecastData] = useState([]);
	const [currentWeatherOutlookData, setcurrentWeatherOutlookData] = useState([]);
	const [currentHumidityData, setCurrentHumidityData] = useState(0);
	const [currentPolutionData, setCurrentPolutionData] = useState(0);
	const [currentTemperatureData, setCurrentTemperatureData] = useState(0);
	const [currentWindData, setCurrentWindData] = useState(0);
	const [sunriseData, setSunriseData] = useState([]);
	const [loading, setLoading] = useState(false);

	const fetchData = async () => {
		try {
			const getLocalWeatherData = await weather.get('fnd&lang=en');
			const getRegionalWeatherData = await weather.get('rhrread&lang=en');
			const getSunriseData = await astronomy.get('4cdWFW/data');
			const getCurrentWeatherOutlookData = await weather.get('flw&lang=en');
			const getOpenWeatherPollutionData = await openWeather.get(`/air_pollution?lat=22.2800&lon=114.1588&appid=${process.env.REACT_APP_OPEN_WEATHER_MAP_API_KEY}`);
			const getOpenWeatherWindData = await openWeather.get(`/onecall?lat=22.2800&lon=114.1588&exclude=hourly,daily&appid=${process.env.REACT_APP_OPEN_WEATHER_MAP_API_KEY}&units=metric`);

			axios.all([getLocalWeatherData, getRegionalWeatherData, getSunriseData, getCurrentWeatherOutlookData, getOpenWeatherPollutionData, getOpenWeatherWindData]).then(
				axios.spread((...allData) => {
					const allLocalWeatherData = allData[0].data.weatherForecast;
					const allSeaTemperaturData = allData[0].data.seaTemp;
					const allSoilTemperatureData = allData[0].data.soilTemp;
					const allRegionalWeatherData = allData[1].data.temperature.data;
					const allCurrentWeatherData = allData[1].data;
					const allHumidityData = allData[1].data.humidity.data[0].value;
					const allSunriseData = allData[2].data;
					const allCurrentWeatherOutlookData = allData[3].data.forecastDesc;
					const allPollutionData = allData[4].data.list[0].components.pm2_5;
					const allCurrentTemperatureData = allData[5].data.current.temp;
					const allWindData = allData[5].data.current.wind_speed;
					const allUvIndexData = allData[5].data.current.uvi;

					setWeatherForecastData(allLocalWeatherData);
					setCurrentSeaTemperature(allSeaTemperaturData);
					setCurrentSoilTemperature(allSoilTemperatureData);
					setRegionalForecastData(allRegionalWeatherData);
					setCurrentUvIndexData(allUvIndexData);
					setCurrentForecastData(allCurrentWeatherData);
					setCurrentHumidityData(allHumidityData);
					setSunriseData(allSunriseData);
					setcurrentWeatherOutlookData(allCurrentWeatherOutlookData);
					setCurrentPolutionData(allPollutionData);
					setCurrentTemperatureData(allCurrentTemperatureData);
					setCurrentWindData(allWindData);
				})
			);
			setLoading(true);
		} catch (error) {
			if (error.response) {
				console.log(error.response.data);
				console.log(error.response.status);
				console.log(error.response.headers);
			} else {
				console.log(`Error: ${error.message}`);
			}
		}
	};

	useEffect(() => {
		fetchData();
	}, []);

	if (!weatherForecastData || !regionalForecastData || !sunriseData || !currentWeatherOutlookData || !currentPolutionData || !currentWindData) return null;

	const LocalWeatherCards = weatherForecastData.map(forecast => <WeatherCard key={forecast.forecastDate} icon={forecast.ForecastIcon} day={forecast.week} maxTemp={forecast.forecastMaxtemp.value} minTemp={forecast.forecastMintemp.value} />);

	const RegionalWeatherCards = regionalForecastData.map(forecast => <WeatherCard key={forecast.place} icon={generateRandomIconNumber(50, 65)} place={forecast.place} maxTemp={forecast.value} />);

	const chartLabels = sunriseData.map(data => data.date);
	const chartSunriseData = sunriseData.map(data => parseInt(data.sunrise.replace(/:/g, '')));
	const chartSunsetData = sunriseData.map(data => parseInt(data.sunset.replace(/:/g, '')));
	const uvIndexData = [currentUvIndexData, 11];

	const sunriseChartData = {
		labels: chartLabels,
		datasets: [
			{
				label: 'Sunrise',
				data: chartSunriseData,
				cubicInterpolationMode: 'monotone',
				borderColor: '#FF8743',
				backgroundColor: '#FF8743',
				pointBorderColor: '#FF8743',
				pointRadius: 2,
				pointBackgroundColor: '#fff',
				pointBorderWidth: 1,
				tension: 0.2,
				color: '#999999',
			},
		],
	};

	const sunsetChartData = {
		labels: chartLabels,
		datasets: [
			{
				label: 'Sunset',
				data: chartSunsetData,
				cubicInterpolationMode: 'monotone',
				borderColor: '#4279EE',
				backgroundColor: '#4279EE',
				pointBorderColor: '#4279EE',
				pointRadius: 2,
				pointBackgroundColor: '#fff',
				pointBorderWidth: 1,
				tension: 0.2,
				color: '#999999',
			},
		],
	};

	const uvIndexChartdata = {
		datasets: [
			{
				data: uvIndexData,
				backgroundColor: ['rgba(90, 156, 253, 1)', 'rgba(250, 250, 250, 1)'],
				hoverBackgroundColor: ['rgba(90, 156, 253, 1)', 'rgba(250, 250, 250, 1)'],
				borderWidth: 0,
			},
		],
	};

	return (
		<div className='l-content'>
			<aside className='l-sidebar'>{loading ? <CurrentWeather icon={currentForecastData.icon} temp={currentTemperatureData.toFixed(0)} outlook={currentWeatherOutlookData} /> : <LoadingElement element='current-weather' />}</aside>
			<main className='l-main'>
				<Tabs firstTab={LocalWeatherCards} secondTab={RegionalWeatherCards} loading={loading} />
				<h2 className='u-mb-24'>Today's Highlights</h2>
				<LayoutGrid columns={3} rows={2} margin='u-mb-32'>
					<LayoutPanel type='chart'>
						<h4>UV Index</h4>
						{loading ? <DonutChart chartData={uvIndexChartdata} /> : <LoadingElement element='donut-chart' />}
					</LayoutPanel>
					<LayoutPanel type='default'>
						<div className='panel__left'>
							<h4>Wind Status</h4>
							<h3>
								{loading ? (
									<span>
										{currentWindData}
										<sup>km/h</sup>
									</span>
								) : (
									<LoadingElement element='h3' />
								)}
							</h3>
							<div className='panel__status'>
								{loading ? (
									<p>
										Status: <span className={`panel__status--${windQualityStatus(currentWindData)}`}>{windQualityStatus(currentWindData)} wind</span>
									</p>
								) : (
									<LoadingElement element='p' />
								)}
							</div>
						</div>
						<div className='panel__right'>
							<StatusChart level={windQualityStatus(currentWindData)} />
						</div>
					</LayoutPanel>
					<LayoutPanel type='default'>
						<div className='panel__left'>
							<h4>Humidity</h4>
							<h3>
								{loading ? (
									<span>
										{currentHumidityData}
										<sup>%</sup>
									</span>
								) : (
									<LoadingElement element='h3' />
								)}
							</h3>
							<div className='panel__status'>
								{loading ? (
									<p>
										Status: <span className={`panel__status--${humidityStatus(currentHumidityData)}`}>{humidityStatus(currentHumidityData)}</span>
									</p>
								) : (
									<LoadingElement element='p' />
								)}
							</div>
						</div>
						<div className='panel__right'>
							<StatusChart level={humidityStatus(currentHumidityData)} />
						</div>
					</LayoutPanel>
					<LayoutPanel type='default'>
						<div className='panel__left'>
							<h4>Sea Temperature</h4>
							<h3>
								{loading ? (
									<span>
										{currentSeaTemperature.value}
										<sup>°C</sup>
									</span>
								) : (
									<LoadingElement element='h3' />
								)}
							</h3>
							<div className='panel__status'>
								{loading ? (
									<p>
										Status: <span className={`panel__status--${temperatureStatus(currentSeaTemperature.value)}`}>{temperatureStatus(currentSeaTemperature.value)}</span>
									</p>
								) : (
									<LoadingElement element='p' />
								)}
							</div>
						</div>
						<div className='panel__right'>
							<StatusChart level={temperatureStatus(currentSeaTemperature.value)} />
						</div>
					</LayoutPanel>
					<LayoutPanel type='default'>
						<div className='panel__left'>
							<h4>Soil Temperature</h4>
							<h3>
								{loading ? (
									<span>
										{currentSoilTemperature[0].value.toFixed(0)}
										<sup>°C</sup>
									</span>
								) : (
									<LoadingElement element='h3' />
								)}
							</h3>
							<div className='panel__status'>
								{loading ? (
									<p>
										Status: <span className={`panel__status--${temperatureStatus(currentSoilTemperature[0].value)}`}>{temperatureStatus(currentSoilTemperature[0].value)}</span>
									</p>
								) : (
									<LoadingElement element='p' />
								)}
							</div>
						</div>
						<div className='panel__right'>
							<StatusChart level={temperatureStatus(currentSoilTemperature[0].value)} />
						</div>
					</LayoutPanel>
					<LayoutPanel type='default'>
						<div className='panel__left'>
							<h4>Air Quality</h4>
							<h3>
								{loading ? (
									<span>
										{currentPolutionData.toFixed(0)}
										<sup>
											PM<sub>25</sub>
										</sup>
									</span>
								) : (
									<LoadingElement element='h3' />
								)}
							</h3>
							<div className='panel__status'>
								{loading ? (
									<p>
										Status: <span className={`panel__status--${airQualityStatus(currentPolutionData)}`}>{airQualityStatus(currentPolutionData)}</span>
									</p>
								) : (
									<LoadingElement element='p' />
								)}
							</div>
						</div>
						<div className='panel__right'>
							<StatusChart level={airQualityStatus(currentPolutionData)} />
						</div>
					</LayoutPanel>
				</LayoutGrid>
				<h2 className='u-mb-24'>Sunrise and Sunset Data</h2>
				<LayoutGrid columns={2} rows={1} margin='u-mb-32'>
					<LayoutPanel type='chart'>
						<h2>Sunrise</h2>
						{loading ? <LineChart chartData={sunriseChartData} /> : <LoadingElement element='line-chart' />}
					</LayoutPanel>
					<LayoutPanel type='chart'>
						<h2>Sunset</h2>
						{loading ? <LineChart chartData={sunsetChartData} /> : <LoadingElement element='line-chart' />}
					</LayoutPanel>
				</LayoutGrid>
				<LayoutPanel type='table'>{loading ? <Table data={sunriseData} /> : <LoadingElement element='table' />}</LayoutPanel>
			</main>
		</div>
	);
}
